<template>
  <button class="lesson-meta-item" @click="handleExportPdf">
    <div class="lesson-meta-item-icon">
      <PrintIcon color="#b90057" />
    </div>
    <span class="lesson-meta-label">Print as a PDF</span>
  </button>

  <!-- Loading Overlay -->
  <LoadingOverlay
    :model-value="isPdfGenerating"
    message="Generating PDF..."
    subtitle="Please wait while we prepare your document"
    :full-screen="true"
    :teleport-to-body="true"
  />
</template>

<script setup>
import html2pdf from "html2pdf.js";
import { ref, defineProps } from "vue";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import { PrintIcon } from "@/components/icons";

/**
 * ExportLessonPdf Component
 *
 * This component handles PDF export of lesson content with dynamic image handling.
 *
 * Image Handling Configuration:
 * - Automatically detects the current environment (development/staging/production)
 * - Configures external domains based on environment
 * - Uses CORS proxy for external images that might have cross-origin restrictions
 *
 * To add new external domains:
 * 1. Update the getImageConfig() function
 * 2. Add domains to the appropriate environment section
 *
 * Supported external image sources:
 * - API servers (staging-api.eddy4teachers.com, api.eddy4teachers.com)
 * - S3 buckets (various configurations)
 * - CDN services
 * - Any external HTTP/HTTPS URL (when useProxyForAllExternal is true)
 *
 * The component will automatically:
 * - Convert external images to base64 for PDF inclusion
 * - Use proxy services for CORS-restricted images
 * - Fallback gracefully when images fail to load
 *
 * Usage Examples:
 *
 * Basic usage:
 * <ExportLessonPdf :lessonData="lesson" :software="software" :categories="categories" />
 *
 * With custom image configuration:
 * <ExportLessonPdf
 *   :lessonData="lesson"
 *   :software="software"
 *   :categories="categories"
 *   :imageConfig="{
 *     externalDomains: ['my-custom-cdn.com', 'another-api.com'],
 *     proxyService: 'https://my-custom-proxy.com/',
 *     useProxyForAllExternal: false
 *   }"
 * />
 */

const props = defineProps({
  lessonData: {
    type: Object,
    required: true,
  },
  software: {
    type: String,
    default: "",
  },
  categories: {
    type: Array,
    default: () => [],
  },
  // Optional: Custom configuration for image handling
  imageConfig: {
    type: Object,
    default: () => ({}),
  },
});

const isPdfGenerating = ref(false);

const getImageConfig = () => {
  const currentOrigin = window.location.origin;
  const isDevelopment = process.env.NODE_ENV === "development";
  const isStaging = currentOrigin.includes("staging");

  // Base configuration
  const baseConfig = {
    proxyService: "https://images.weserv.nl/",
    appOrigin: currentOrigin,
    imageTimeout: 15000,
    useProxyForAllExternal: true,
  };

  let externalDomains = [];

  if (isDevelopment) {
    externalDomains = [
      "staging-api.eddy4teachers.com",
      "localhost",
      "127.0.0.1",
    ];
  } else if (isStaging) {
    externalDomains = ["staging-api.eddy4teachers.com"];
  } else {
    externalDomains = ["api.eddy4teachers.com", "s3.amazonaws.com"];
  }

  const defaultConfig = {
    ...baseConfig,
    externalDomains,
    environment: isDevelopment
      ? "development"
      : isStaging
        ? "staging"
        : "production",
  };

  const customConfig = props.imageConfig || {};

  if (
    customConfig.externalDomains &&
    Array.isArray(customConfig.externalDomains)
  ) {
    customConfig.externalDomains = [
      ...externalDomains,
      ...customConfig.externalDomains,
    ];
  }

  return {
    ...defaultConfig,
    ...customConfig,
  };
};

const isExternalImageUrl = (url) => {
  if (!url || url.startsWith("data:") || url.startsWith("blob:")) {
    return false;
  }

  const config = getImageConfig();

  if (url.startsWith("/") || url.startsWith("./") || url.startsWith("../")) {
    return false;
  }

  if (url.startsWith(config.appOrigin)) {
    return false;
  }

  const isKnownExternal = config.externalDomains.some((domain) =>
    url.includes(domain)
  );

  const isAbsoluteExternal =
    config.useProxyForAllExternal &&
    url.startsWith("http") &&
    !url.startsWith(config.appOrigin);

  const result = isKnownExternal || isAbsoluteExternal;

  if (config.environment === "development" && result) {
    console.log(`[PDF Export] Detected external image URL: ${url}`);
    console.log(`[PDF Export] Environment: ${config.environment}`);
    console.log(`[PDF Export] Known external domains:`, config.externalDomains);
  }

  return result;
};

const getFilteredCategories = () => {
  return props.categories.filter((category) => category.name !== "software");
};

const convertImageToDataUrl = (imgElement) => {
  return new Promise((resolve) => {
    if (imgElement.complete && imgElement.naturalHeight !== 0) {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = imgElement.naturalWidth;
        canvas.height = imgElement.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(imgElement, 0, 0);
        resolve(canvas.toDataURL("image/jpeg", 0.95));
      } catch (e) {
        console.error("Error converting image to data URL:", e);
        resolve(imgElement.src);
      }
    } else {
      const newImg = new Image();
      newImg.crossOrigin = "Anonymous";

      newImg.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = newImg.naturalWidth;
          canvas.height = newImg.naturalHeight;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(newImg, 0, 0);
          resolve(canvas.toDataURL("image/jpeg", 0.95));
        } catch (e) {
          console.error("Error converting image to data URL:", e);
          resolve(imgElement.src);
        }
      };

      newImg.onerror = () => {
        console.error("Error loading image:", imgElement.src);
        resolve(imgElement.src);
      };

      if (imgElement.src.startsWith("/")) {
        newImg.src = window.location.origin + imgElement.src;
      } else {
        newImg.src = imgElement.src;
      }
    }
  });
};

const createProxyImageUrl = (originalUrl) => {
  if (originalUrl.startsWith("data:")) return originalUrl;

  // Only proxy external images that need it
  if (isExternalImageUrl(originalUrl)) {
    const config = getImageConfig();
    return `${config.proxyService}?url=${encodeURIComponent(
      originalUrl
    )}&default=${encodeURIComponent(originalUrl)}`;
  }

  return originalUrl;
};

const fetchImageAsBase64 = async (imageUrl) => {
  try {
    // Use proxy for external URLs that might have CORS issues
    const url = isExternalImageUrl(imageUrl)
      ? createProxyImageUrl(imageUrl)
      : imageUrl;

    const response = await fetch(url, {
      mode: "cors",
      cache: "no-cache",
      headers: {
        Accept: "image/jpeg,image/png,image/svg+xml,image/*;q=0.8",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }

    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Error fetching image:", error);
    return null;
  }
};

const createDirectSlideElement = (slideDeck) => {
  const base64ImageData = slideDeck.getAttribute("data-base64-image");

  const thumbnailUrl =
    slideDeck.getAttribute("thumbnailurl") ||
    slideDeck
      .querySelector(".slide-deck-thumbnail")
      ?.getAttribute("data-thumbnailurl");

  const slideTitle =
    slideDeck.getAttribute("title") ||
    slideDeck.querySelector(".slide-deck-title")?.textContent ||
    "Slide Preview";

  const imgElement = slideDeck.querySelector("img.slide-thumbnail-img");
  const imgSrc = thumbnailUrl || (imgElement ? imgElement.src : null);

  const notesElement = slideDeck.querySelector(".slide-deck-notes");
  let notesHtml = "";

  if (notesElement) {
    const childElements = Array.from(notesElement.children);
    const filteredElements = childElements.filter((el) => {
      const text = el.textContent?.trim();
      return text && text.length > 0;
    });

    if (filteredElements.length > 0) {
      notesHtml = filteredElements
        .map((el) => {
          const cleanText = el.textContent?.trim() || "";
          if (cleanText) {
            return `<p style="margin-bottom: 10px; line-height: 1.4;">${cleanText}</p>`;
          }
          return "";
        })
        .filter((html) => html)
        .join("");
    }
  }

  if (!notesHtml) {
    const notesAttr = slideDeck.getAttribute("notes");
    if (notesAttr && notesAttr.trim()) {
      const notesParagraphs = notesAttr
        .trim()
        .split("\n")
        .filter((line) => line.trim());
      notesHtml = notesParagraphs
        .map(
          (paragraph) =>
            `<p style="margin-bottom: 10px; line-height: 1.4;">${paragraph.trim()}</p>`
        )
        .join("");
    }
  }

  if (!imgSrc && !base64ImageData) {
    return `<div class="pdf-slide-fallback">
              <p><em>Slide preview not available in PDF. Please check the online version.</em></p>
            </div>`;
  }

  let imageHtml = "";

  if (base64ImageData) {
    imageHtml = `<img src="${base64ImageData}" 
                      alt="${slideTitle}" 
                      style="width: 100%; height: auto; display: block; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">`;
  } else {
    const proxiedSrc = createProxyImageUrl(imgSrc);

    // Check if this is an external image that needs special handling
    const isExternal = isExternalImageUrl(imgSrc);
    imageHtml = isExternal
      ? `<img src="${proxiedSrc}" 
              alt="${slideTitle}" 
              style="width: 100%; height: auto; display: block; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);"
              crossorigin="anonymous"
              onerror="this.onerror=null; this.style.display='none'; this.nextElementSibling.style.display='block';">
         <div style="display:none; padding: 20px; background: #f5f5f5; text-align: center; border-radius: 8px;">
           <p><strong>Image failed to load</strong></p>
           <p>You can view this slide in the online version.</p>
         </div>`
      : `<img src="${proxiedSrc}" 
              alt="${slideTitle}" 
              style="width: 100%; height: auto; display: block; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);"
              crossorigin="anonymous">`;
  }

  return `<div class="pdf-slide-container" style="display: flex; gap: 20px; margin: 20px 0; padding: 15px; background: #f9f9f9; border-radius: 10px; border-left: 4px solid #4285f4;">
            <div class="pdf-slide-image-section" style="flex: 0 0 45%; min-width: 200px;">
              <h4 style="margin: 0 0 10px 0; color: #333; font-size: var(--fs-medium);">${slideTitle}</h4>
              ${imageHtml}
            </div>
            <div class="pdf-slide-notes-section" style="flex: 1; padding-left: 10px;">
              <h5 style="margin: 0 0 10px 0; color: #666; font-size: var(--fs-small); font-weight: 600;">Notes:</h5>
              <div class="pdf-slide-notes-content" style="color: #555; font-size: 13px;">
                ${
                  notesHtml ||
                  '<p style="font-style: italic; color: #999;">No notes available for this slide.</p>'
                }
              </div>
            </div>
          </div>`;
};

const handleExportPdf = async () => {
  isPdfGenerating.value = true;

  try {
    const allSlideContainers = document.querySelectorAll(
      ".slide-deck-container"
    );
    if (allSlideContainers.length > 0) {
      console.log(
        `Found ${allSlideContainers.length} slide containers to process`
      );

      for (const slideContainer of allSlideContainers) {
        const thumbnailUrl = slideContainer.getAttribute("thumbnailurl");
        const imgElement = slideContainer.querySelector(
          "img.slide-thumbnail-img"
        );

        if (thumbnailUrl || (imgElement && imgElement.src)) {
          const imgSrc = thumbnailUrl || imgElement.src;

          // Use dynamic detection instead of hardcoded domain check
          if (isExternalImageUrl(imgSrc)) {
            try {
              const base64Data = await fetchImageAsBase64(imgSrc);
              if (base64Data) {
                console.log("Successfully loaded external image as base64");

                slideContainer.setAttribute("data-base64-image", base64Data);

                if (imgElement) {
                  const newImg = document.createElement("img");
                  newImg.src = base64Data;
                  newImg.alt = imgElement.alt || "Slide preview";
                  newImg.className = imgElement.className;
                  newImg.style.maxWidth = "100%";
                  newImg.style.height = "auto";
                  imgElement.parentNode.replaceChild(newImg, imgElement);
                }
              }
            } catch (err) {
              console.error("Failed to preload external image:", err);
            }
          }
        }
      }
    }

    const pdfContainer = document.createElement("div");
    pdfContainer.className = "pdf-export-container";

    const titleElement = document.createElement("h1");
    titleElement.textContent = props.lessonData.title;
    pdfContainer.appendChild(titleElement);

    if (props.software) {
      const softwareInfo = document.createElement("div");
      softwareInfo.className = "pdf-software-info";
      softwareInfo.innerHTML = `<strong>Software:</strong> ${props.software}`;
      pdfContainer.appendChild(softwareInfo);
    }

    const contentInfoSection = document.createElement("div");
    contentInfoSection.className = "pdf-content-info";
    contentInfoSection.innerHTML = "<h2>Content Information</h2>";

    const categoriesList = document.createElement("div");
    categoriesList.className = "pdf-categories";

    getFilteredCategories().forEach((category) => {
      const categoryItem = document.createElement("div");
      categoryItem.className = "pdf-category-item";

      const categoryTitle = document.createElement("h3");
      categoryTitle.textContent = category.title;
      categoryItem.appendChild(categoryTitle);

      const termsContainer = document.createElement("div");
      termsContainer.className = "pdf-terms";

      category.terms.forEach((term) => {
        const termSpan = document.createElement("span");
        termSpan.className = "pdf-term-tag";
        termSpan.textContent = term.title;
        termsContainer.appendChild(termSpan);
      });

      categoryItem.appendChild(termsContainer);
      categoriesList.appendChild(categoryItem);
    });

    contentInfoSection.appendChild(categoriesList);
    pdfContainer.appendChild(contentInfoSection);

    if (props.lessonData.tabs && props.lessonData.tabs.length > 0) {
      for (let index = 0; index < props.lessonData.tabs.length; index++) {
        const tab = props.lessonData.tabs[index];

        const tabSection = document.createElement("div");
        tabSection.className = "pdf-tab-section";

        const tabTitle = document.createElement("h2");
        tabTitle.textContent = tab.name;
        tabSection.appendChild(tabTitle);

        if (tab.duration) {
          const durationInfo = document.createElement("div");
          durationInfo.className = "pdf-duration";
          durationInfo.innerHTML = `<strong>Duration:</strong> ${tab.duration} Minutes`;
          tabSection.appendChild(durationInfo);
        }

        if (
          tab.type === "assessment" &&
          tab.assessments &&
          tab.assessments.length > 0
        ) {
          const assessmentsContainer = document.createElement("div");
          assessmentsContainer.className = "pdf-assessments";

          tab.assessments.forEach((assessment) => {
            const assessmentElement = createAssessmentContent(assessment);
            assessmentsContainer.appendChild(assessmentElement);
          });

          tabSection.appendChild(assessmentsContainer);
        } else if (tab.sections && tab.sections.length > 0) {
          for (
            let sectionIndex = 0;
            sectionIndex < tab.sections.length;
            sectionIndex++
          ) {
            const section = tab.sections[sectionIndex];
            const sectionDiv = document.createElement("div");
            sectionDiv.className = "pdf-section";

            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = section.content;

            const accordions = tempDiv.querySelectorAll(".tooltip-block");
            accordions.forEach((accordion) => {
              const title = accordion.querySelector(".tooltip-title");
              const body = accordion.querySelector(".tooltip-body");

              if (title && body) {
                const expandedAccordion = document.createElement("div");
                expandedAccordion.className = "pdf-expanded-accordion";

                const accordionTitle = document.createElement("h4");
                accordionTitle.innerHTML = title.innerHTML;
                expandedAccordion.appendChild(accordionTitle);

                const accordionContent = document.createElement("div");
                accordionContent.innerHTML = body.innerHTML;
                expandedAccordion.appendChild(accordionContent);

                accordion.parentNode.replaceChild(expandedAccordion, accordion);
              }
            });

            const commentIcons = tempDiv.querySelectorAll(
              ".comment-icon-wrapper"
            );
            commentIcons.forEach((icon) => icon.remove());

            const interactiveElements = tempDiv.querySelectorAll(
              'button, input, select, textarea, [onclick], [contenteditable="true"]'
            );
            interactiveElements.forEach((el) => {
              if (el.tagName === "BUTTON" && el.textContent.trim()) {
                const span = document.createElement("span");
                span.textContent = el.textContent;
                el.parentNode.replaceChild(span, el);
              } else {
                el.removeAttribute("onclick");
                el.removeAttribute("contenteditable");
              }
            });

            const slideDecks = tempDiv.querySelectorAll(
              ".slide-deck-container"
            );
            for (let i = 0; i < slideDecks.length; i++) {
              const slideDeck = slideDecks[i];

              try {
                const directSlideHtml = createDirectSlideElement(slideDeck);
                slideDeck.innerHTML = directSlideHtml;
              } catch (error) {
                console.error("Error processing slide image:", error);
                slideDeck.innerHTML =
                  '<div class="pdf-slide-fallback"><p><em>Slide preview not available in PDF. Please check the online version.</em></p></div>';
              }
            }

            const processedHtml = await preloadImages(tempDiv.innerHTML);
            sectionDiv.innerHTML = processedHtml;
            tabSection.appendChild(sectionDiv);
          }
        }

        pdfContainer.appendChild(tabSection);
      }
    }

    const style = document.createElement("style");
    style.textContent = `
      .pdf-export-container {
        font-family: Arial, sans-serif;
        color: #333;
        line-height: 1.6;
        padding: 20px;
      }
      .pdf-export-container h1 {
        color: #333;
        font-size: var(--fs-xxl);
        margin-bottom: 20px;
        border-bottom: 2px solid #f0f0f0;
        padding-bottom: 10px;
      }
      .pdf-export-container h2 {
        color: #444;
        font-size: var(--fs-xl);
        margin: 30px 0 15px;
        border-bottom: 1px solid #f0f0f0;
        padding-bottom: 8px;
      }
      .pdf-export-container h3 {
        color: #555;
        font-size: var(--fs-big);
        margin: 20px 0 10px;
      }
      .pdf-export-container h4 {
        color: #666;
        font-size: 18px;
        margin: 15px 0 10px;
        font-weight: bold;
      }
      .pdf-software-info {
        margin-bottom: 20px;
        padding: 10px;
        background-color: #f8f9fa;
        border-radius: 5px;
      }
      .pdf-category-item {
        margin-bottom: 20px;
      }
      .pdf-term-tag {
        display: inline-block;
        background-color: #f0f0f0;
        padding: 5px 10px;
        margin: 0 5px 5px 0;
        border-radius: 4px;
        font-size: var(--fs-small);
      }
      .pdf-duration {
        margin-bottom: 20px;
        font-style: italic;
        color: #666;
      }
      .pdf-expanded-accordion {
        margin: 15px 0;
        padding: 10px;
        background-color: #f8f9fa;
        border-left: 4px solid #ddd;
      }
      .pdf-tab-section {
        margin-bottom: 40px;
        page-break-after: always;
      }
      .pdf-section {
        margin-bottom: 20px;
      }
      .pdf-section img {
        max-width: 100%;
        height: auto;
      }
      .pdf-section table {
        width: 100%;
        border-collapse: collapse;
        margin: 15px 0;
      }
      .pdf-section table, .pdf-section th, .pdf-section td {
        border: 1px solid #ddd;
      }
      .pdf-section th, .pdf-section td {
        padding: 8px;
        text-align: left;
      }
      .pdf-section th {
        background-color: #f2f2f2;
      }
      .pdf-slide-container {
        margin: 20px 0;
        padding: 15px;
        background: #f9f9f9;
        border-radius: 10px;
        border-left: 4px solid #4285f4;
        display: flex;
        gap: 20px;
        align-items: flex-start;
      }
      .pdf-slide-image-section {
        flex: 0 0 45%;
        min-width: 200px;
      }
      .pdf-slide-image-section h4 {
        margin: 0 0 10px 0;
        color: #333;
        font-size: var(--fs-medium);
        font-weight: 600;
      }
      .pdf-slide-image-section img {
        width: 100%;
        height: auto;
        display: block;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      }
      .pdf-slide-notes-section {
        flex: 1;
        padding-left: 10px;
      }
      .pdf-slide-notes-section h5 {
        margin: 0 0 10px 0;
        color: #666;
        font-size: var(--fs-small);
        font-weight: 600;
      }
      .pdf-slide-notes-content {
        color: #555;
        font-size: 13px;
        line-height: 1.5;
      }
      .pdf-slide-notes-content p {
        margin-bottom: 10px;
        line-height: 1.4;
      }
      .pdf-slide-fallback {
        text-align: center;
        margin: 20px 0;
        padding: 15px;
        background: #f9f9f9;
        border: 1px dashed #ddd;
        color: #666;
      }
      .pdf-assessment-container {
        margin-bottom: 25px;
        padding: 15px;
        background-color: #f9f9f9;
        border-radius: 5px;
        border-left: 4px solid #4caf50;
      }
      .pdf-assessment-title {
        margin-bottom: 15px;
        color: #4caf50;
      }
      .pdf-assessment-title h4 {
        margin: 0;
        font-size: 18px;
      }
      .pdf-assessment-items {
        display: flex;
        flex-direction: column;
        gap: 15px;
      }
      .pdf-assessment-item {
        padding: 10px;
        background: white;
        border-radius: 4px;
        border-bottom: 1px solid #eee;
      }
      .pdf-assessment-item-header {
        display: flex;
        align-items: center;
        margin-bottom: 8px;
        font-weight: 600;
      }
      .pdf-assessment-item-content {
        margin-left: 30px;
        color: #555;
      }
      
      /* Responsive adjustments for smaller content */
      @media print {
        .pdf-slide-container {
          break-inside: avoid;
          page-break-inside: avoid;
        }
        .pdf-assessment-container {
          break-inside: avoid;
          page-break-inside: avoid;
        }
      }
    `;
    pdfContainer.appendChild(style);

    document.body.appendChild(pdfContainer);

    await new Promise((resolve) => setTimeout(resolve, 3000));

    const options = {
      margin: [15, 15],
      filename: `${props.lessonData.title || "Lesson"}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: true,
        imageTimeout: getImageConfig().imageTimeout,
        foreignObjectRendering: false,
        onclone: function (clonedDoc) {
          const images = clonedDoc.querySelectorAll("img");
          images.forEach((img) => {
            img.style.display = "block";
            img.style.maxWidth = "100%";
            img.setAttribute("crossorigin", "anonymous");

            // Use dynamic detection for external images
            if (isExternalImageUrl(img.src)) {
              img.style.width = "auto";
              img.style.maxHeight = "400px";
              img.style.objectFit = "contain";

              const originalImgContainer = img.closest(".slide-deck-container");
              if (originalImgContainer) {
                const originalContainer = document.querySelector(
                  `[data-block-id="${originalImgContainer.getAttribute(
                    "data-block-id"
                  )}"]`
                );
                if (originalContainer) {
                  const base64Data =
                    originalContainer.getAttribute("data-base64-image");
                  if (base64Data) {
                    img.src = base64Data;
                  }
                }
              }
            }
          });
        },
      },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };

    await html2pdf().from(pdfContainer).set(options).save();
  } catch (error) {
    console.error("Error generating PDF:", error);
    alert("Error generating PDF. Please try again.");
  } finally {
    const pdfContainer = document.querySelector(".pdf-export-container");
    if (pdfContainer && pdfContainer.parentNode) {
      pdfContainer.parentNode.removeChild(pdfContainer);
    }
    isPdfGenerating.value = false;
  }
};

const createAssessmentContent = (assessment) => {
  const assessmentContainer = document.createElement("div");
  assessmentContainer.className = "pdf-assessment-container";

  const titleDiv = document.createElement("div");
  titleDiv.className = "pdf-assessment-title";
  titleDiv.innerHTML = `<h4>${assessment.title}</h4>`;
  assessmentContainer.appendChild(titleDiv);

  if (assessment.items && assessment.items.length > 0) {
    const itemsContainer = document.createElement("div");
    itemsContainer.className = "pdf-assessment-items";

    const itemHeadersData = [
      { text: "Emerging", icon: "/images/icon/emerging.svg" },
      { text: "Developing", icon: "/images/icon/developing.svg" },
      { text: "Proficient", icon: "/images/icon/proficient.svg" },
    ];

    assessment.items.forEach((item, index) => {
      const itemDiv = document.createElement("div");
      itemDiv.className = "pdf-assessment-item";

      const headerData = itemHeadersData[index % itemHeadersData.length];
      const headerDiv = document.createElement("div");
      headerDiv.className = "pdf-assessment-item-header";

      const iconSrc = headerData.icon.startsWith("/")
        ? window.location.origin + headerData.icon
        : headerData.icon;

      headerDiv.innerHTML = `
        <img src="${iconSrc}" alt="${headerData.text}" style="width: 20px; height: 20px; margin-right: 10px;" />
        <strong>${headerData.text}</strong>
      `;
      itemDiv.appendChild(headerDiv);

      const contentDiv = document.createElement("div");
      contentDiv.className = "pdf-assessment-item-content";
      contentDiv.textContent = item.text;
      itemDiv.appendChild(contentDiv);

      itemsContainer.appendChild(itemDiv);
    });

    assessmentContainer.appendChild(itemsContainer);
  }

  return assessmentContainer;
};

const preloadImages = async (html) => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  const images = tempDiv.querySelectorAll("img");
  const imagePromises = Array.from(images).map(async (img) => {
    try {
      if (img.src.startsWith("/")) {
        img.src = window.location.origin + img.src;
      }

      // Use dynamic detection for external images
      if (isExternalImageUrl(img.src)) {
        img.src = createProxyImageUrl(img.src);
      }

      const dataUrl = await convertImageToDataUrl(img);
      img.src = dataUrl;

      img.setAttribute("crossorigin", "anonymous");
      img.style.maxWidth = "100%";
      img.style.height = "auto";
      img.style.display = "block";
    } catch (error) {
      console.error("Error preloading image:", error);
    }
  });

  await Promise.race([
    Promise.all(imagePromises),
    new Promise((resolve) => setTimeout(resolve, 8000)),
  ]);

  return tempDiv.innerHTML;
};
</script>

<style scoped>
.lesson-meta-item {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--pink-dark-1);
  background-color: var(--white-hover-1);
  border-radius: 100px;
  padding: 4px;
}

.lesson-meta-item-icon {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lesson-meta-label {
  margin-right: 4px;
  font-size: var(--fs-small);
}
</style>
